import React from 'react';
import styled from 'styled-components/macro';
import { DateTime } from 'luxon';

import { Label } from '../../SubComponents/Text';
import Loading from '../../SubComponents/Loading';
import Interval from './interval';
import { TimeslotInterval } from './arrange-in-intervals';
import useTypeSafeLocalize from '../../Hooks/use-type-safe-localize';
import {
  ErrorLocalization,
  LocalizationCategory,
  isErrorLocalization,
} from '../../localizations/types';

export interface ParsedTimeslot {
  startTime: DateTime;
  bookable: boolean;
  vacancies: number;
  price: number;
  priceModelId: string;
  isBlockedByMinimumNotice: boolean;
  customerFacingText?: string | null;
}

export interface TimeslotsProps {
  className?: string;
  bookingDate: string | null;
  boatCount: number;
  duration: number;
  intervals: TimeslotInterval[];
  onChange: (timeslot: ParsedTimeslot) => void;
  loading: boolean;
  helpText?: string;
  error?: string;
  enabled: boolean;
  vacancyThreshold: number | null | undefined;
  localizedTimeFormat: boolean;
}

const Timeslots = (props: TimeslotsProps) => {
  const {
    className,
    bookingDate,
    boatCount,
    duration,
    intervals,
    onChange,
    loading,
    helpText,
    error,
    enabled = true,
    vacancyThreshold,
    localizedTimeFormat,
  } = props;
  const localize = useTypeSafeLocalize();

  return (
    <Loading loading={loading} delay={300} className={className} _id="timeslots">
      {error ? (
        <Label error m="0 auto" textAlign="center">
          {localize(
            LocalizationCategory.error,
            isErrorLocalization(error) ? error : ErrorLocalization.UnknownError
          )}
        </Label>
      ) : intervals?.length > 0 ? (
        intervals.map((interval) => (
          <Interval
            interval={interval}
            bookingDate={bookingDate ? new Date(bookingDate) : null}
            duration={duration}
            key={interval.timeslots[0].startTime.toISO()}
            onChange={onChange}
            enabled={enabled}
            boatCount={boatCount}
            vacancyThreshold={vacancyThreshold}
            localizedTimeFormat={localizedTimeFormat}
            customerFacingText={interval.customerFacingText}
          />
        ))
      ) : !loading ? (
        <Label m="0 auto" textAlign="center">
          {helpText}
        </Label>
      ) : (
        /* Empty div so the loading spinner have a children prop to wrap around */
        <div />
      )}
    </Loading>
  );
};

const StyledTimeslots = styled(Timeslots)`
  flex: 1 1 50%;
  max-width: 358px;
  margin-left: 8px;
  text-align: center;

  @media screen and (max-width: 767px) {
    max-width: none;
    margin: 0;
    width: 100%;
  }
`;

export default StyledTimeslots;
